import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { motion } from "framer-motion";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Home, Compass, ListChecks, Scale, MessageCircleQuestion, ShieldCheck } from "lucide-react";

const features = [
  { icon: Compass, title: "Guided roadmap", text: "Always know the stage you're in and the one next step to take." },
  { icon: ListChecks, title: "Readiness checklist", text: "Pre-approval, budget clarity, touring prep and documents in one place." },
  { icon: Scale, title: "Compare homes clearly", text: "Score homes against your own must-haves — not someone else's opinion." },
  { icon: MessageCircleQuestion, title: "Plain-language answers", text: "Ask process questions anytime, and escalate to your agent when it matters." },
];

export default function Landing() {
  const { data: welcome } = useQuery({ queryKey: ["public-welcome"], queryFn: async () => (await api.get("/public/welcome")).data });
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="flex items-center justify-between px-5 sm:px-10 py-5 max-w-7xl mx-auto">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-xl bg-primary flex items-center justify-center">
            <Home className="h-5 w-5 text-primary-foreground" />
          </div>
          <span className="font-heading text-xl font-bold tracking-tight">Look Before You Leap 808</span>
        </div>
        <div className="flex items-center gap-2">
          <ThemeToggle />
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-5 sm:px-10 pt-10 sm:pt-16 pb-20 grid lg:grid-cols-2 gap-12 items-center">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-forest bg-forest/10 px-3 py-1 rounded-full">
            <ShieldCheck className="h-3.5 w-3.5" /> For all homebuyers · Hawai‘i
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] mt-5">
            Buy your home with <span className="text-primary">calm, clear</span> guidance.
          </h1>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed mt-5 max-w-xl">
            Look Before You Leap 808 is your private workspace with your agent — whether it's your first home, a
            move-up, or a military or relocation move. Get ready, compare homes against
            your own priorities, and know exactly when you're ready to move forward. No jargon, no pressure.
          </p>
          <p className="text-sm text-muted-foreground mt-8 max-w-md">
            Access is by private invitation only. Clients: open the invite link Marlene sent you. New client?{" "}
            <span className="text-foreground font-medium">Ask Marlene for access.</span>
          </p>
          <p className="text-xs text-muted-foreground mt-3">
            Informational workspace only — no neighborhood ratings, no legal or financial advice.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="relative"
        >
          <div className="hg-card overflow-hidden rounded-3xl max-w-[240px] mx-auto">
            <img
              src="/agent-photo.png"
              alt="Marlene Alesna, your real estate agent"
              className="w-full h-52 object-cover"
            />
            <div className="p-3 bg-forest text-white text-center">
              <p className="font-heading font-semibold">Marlene Alesna</p>
              <p className="text-sm text-white/90">Your agent · Realtor</p>
              <p className="text-xs text-white/70 mt-0.5">RS-88409 · eXp Realty · The Agency Team Hawaii</p>
            </div>
          </div>
          {welcome?.message && (
            <motion.figure
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.35 }}
              className="hg-card relative max-w-md mx-auto mt-6 p-6 rounded-3xl"
              data-testid="landing-welcome-note"
            >
              <span aria-hidden className="absolute -top-4 left-6 font-heading text-6xl leading-none text-primary/30">“</span>
              <blockquote className="text-base leading-relaxed text-foreground whitespace-pre-line" data-testid="landing-welcome-text">
                {welcome.message}
              </blockquote>
              <figcaption className="mt-4 font-heading italic text-primary">— Marlene</figcaption>
            </motion.figure>
          )}
        </motion.div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-10 pb-24 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {features.map((f, i) => (
          <motion.div
            key={f.title}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: i * 0.08 }}
            className="hg-card hg-card-hover p-6"
          >
            <div className="h-11 w-11 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
              <f.icon className="h-5 w-5 text-primary" />
            </div>
            <h3 className="font-heading text-lg font-semibold">{f.title}</h3>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{f.text}</p>
          </motion.div>
        ))}
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-10 pb-24">
        <div className="relative rounded-3xl overflow-hidden h-56 sm:h-72">
          <img src="https://images.unsplash.com/photo-1562706948-81a8064d067f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" alt="Hawaii coastline with palms" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-forest/50 flex items-center justify-center text-center px-6">
            <p className="font-heading text-2xl sm:text-3xl font-bold text-white max-w-2xl leading-tight">Island-grown guidance for your next home in the 808.</p>
          </div>
        </div>
      </section>

      <footer className="border-t border-border py-8 text-center text-sm text-muted-foreground">
        <p>Look Before You Leap 808 · A private workspace for homebuyers in Hawai‘i and their agent, Marlene Alesna. Final decisions always belong to you.</p>
        <div className="flex items-center justify-center gap-4 mt-2">
          <Link to="/login" className="text-primary hover:underline">Agent login</Link>
          <span className="text-border">·</span>
          <a href="https://knowbeforeyoutour808.com" target="_blank" rel="noreferrer" className="text-primary hover:underline" data-testid="existing-site-link">Visit knowbeforeyoutour808.com</a>
        </div>
      </footer>
    </div>
  );
}
